import { StateCreator } from "zustand";
import { produce } from "immer";
import { wrapStore } from "@/app/store/StoreWrapper";

export interface DownloadStatus {
	status: string;
	translationFallback: string;
	downloadRate?: number;
	progress?: number;
	total?: number;
	done?: number;
	downloadSecondLeft?: number;
	checkingTotal?: number;
	checkingCurrent?: number;
	failed?: number;
}

interface ITorrentAction {
	type: "idle"|"stop"|"restart";
}

export interface IFxLauncherLoaderStore {
	filesChecked: boolean;
	torrentId: number;
	torrentAction: ITorrentAction;
	setTorrentAction: (action: ITorrentAction) => void;
	downloadingTorrentId: number;
	status: DownloadStatus;
	filesCheckProgress: number;
	torrentsProgress: number;
	downloadSecondLeft: number;
	torrentStarted: boolean;
	torrentLoaded: boolean;
	setFilesChecked: (checked: boolean) => void;
	setTorrentId: (torrentId: number) => void;
	setDownloadingTorrentId: (torrentId: number) => void;
	setStatus: (status: DownloadStatus) => void;
	setFilesCheckProgress: (progress: number) => void;
	setTorrentsProgress: (progress: number) => void;
	setDownloadSecondLeft: (seconds: number) => void;
	setTorrentStarted: (started: boolean) => void;
	setTorrentLoaded: (loaded: boolean) => void;
	downloadSpeedHistory: number[];
	setDownloadSpeedHistory: (history: number) => void;
	avgDownloadSpeed: number;
	badSpeedInRaw: number;
	setBadSpeedInRaw: (value: number) => void;
	autoStart: boolean;
	setAutoStart: (autoStart: boolean) => void,
}

export const createFxLauncherLoaderStore: StateCreator<IFxLauncherLoaderStore> = (set) => ({
	filesChecked: false,
	autoStart: false,
	setAutoStart: (autoStart: boolean) => {
		set(
            produce((store: IFxLauncherLoaderStore) => {
                store.autoStart = autoStart;
            })
        );
	},
	torrentAction: { type: "idle" },
    setTorrentAction: (action: ITorrentAction) => {
		set(
            produce((store: IFxLauncherLoaderStore) => {
                store.torrentAction = action;
            })
        );
	},
	torrentId: -1,
	downloadingTorrentId: -1,
	status: { status: "common.Waiting",translationFallback: "Waiting" },
	filesCheckProgress: 0,
	torrentsProgress: 0,
	downloadSecondLeft: 0,
	torrentStarted: false,
	torrentLoaded: false,
	downloadSpeedHistory: [0, 0, 0, 0, 0, 0, 0, 0, 0],
	avgDownloadSpeed: -1,
	badSpeedInRaw: 0,
	setBadSpeedInRaw: (value: number) => {
		set(
            produce((store: IFxLauncherLoaderStore) => {
                store.badSpeedInRaw = value;
				if(value == 0 )
				{
					store.avgDownloadSpeed = 0;
				}
            })
        );
	},
	setDownloadSpeedHistory: (value: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				let history = store.downloadSpeedHistory;
				if (history.length > 100) {
					history = history.slice(1);
				}
				history.push(value);
				store.avgDownloadSpeed =
					store.avgDownloadSpeed === -1
						? value
						: (store.avgDownloadSpeed * (history.length - 1) + value) / history.length;
				if(value<store.avgDownloadSpeed/2)
				{
					store.badSpeedInRaw = store.badSpeedInRaw + 1;
				}
				else
				{
					store.badSpeedInRaw = Math.round(store.badSpeedInRaw/2) || 0;
				}
				store.downloadSpeedHistory = history;
			})
		);
	},

	setFilesChecked: (checked: boolean) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.filesChecked = checked;
			})
		);
	},

	setTorrentId: (torrentId: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.torrentId = torrentId;
			})
		);
	},
	setDownloadingTorrentId: (downloadingTorrentID: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.downloadingTorrentId = downloadingTorrentID;
			})
		);
	},

	setStatus: (status: DownloadStatus) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.status = status;
			})
		);
	},

	setFilesCheckProgress: (progress: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.filesCheckProgress = progress;
			})
		);
	},

	setTorrentsProgress: (progress: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.torrentsProgress = progress;
			})
		);
	},

	setDownloadSecondLeft: (seconds: number) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.downloadSecondLeft = seconds;
			})
		);
	},

	setTorrentStarted: (started: boolean) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.torrentStarted = started;
			})
		);
	},

	setTorrentLoaded: (loaded: boolean) => {
		set(
			produce((store: IFxLauncherLoaderStore) => {
				store.torrentLoaded = loaded;
			})
		);
	}
});

export const fxLauncherLoaderStoreWrapper = wrapStore<IFxLauncherLoaderStore>();
export const fxLauncherLoaderStore = fxLauncherLoaderStoreWrapper.withStore(
	createFxLauncherLoaderStore,
	"fx-launcher-loader-store"
);
